import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toastApiError } from "@/lib/errorMessages";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui";
import { PageFrame } from "@/components/common";
import { useAuthStore } from "@/store/authStore";
import {
  adminSettlementKeys,
  adminSettlementService,
  type SettlementLineRow,
  type SettlementPayeeLinesResponse,
} from "@/services/adminSettlementService";
import { Permission } from "@/types";
import { resolveSelectionFlow } from "./settlementSelection";
import {
  filterSettlementLines,
  isSettlementLineFinalizedAndPaid,
  settlementLineIdsForMetric,
  settlementSelectionTotals,
  settlementSummaryEmailStats,
  sortSettlementLinesByEventDate,
  type SettlementFlowFilter,
  type SettlementInvoiceFilter,
  type SettlementMetricFilter,
  type SettlementPaymentFilter,
} from "./settlementUi";
import { SettlementActionSteps } from "./detail/SettlementActionSteps";
import { SettlementLinesSection } from "./detail/SettlementLinesSection";
import { SettlementPayeeHeader } from "./detail/SettlementPayeeHeader";
import { SettlementRevolutBanner } from "./detail/SettlementRevolutBanner";
import { openOrDownload } from "@/lib/downloadBlob";
import { useRevolutPayoutPolling } from "./detail/useRevolutPayoutPolling";
import { useSettlementActions } from "./detail/useSettlementActions";
import { isTerminalRevolutStatus, selectionAfterReload } from "./detail/utils";

const EMPTY_LINES: SettlementLineRow[] = [];

type SettlementDocumentKind = "commission" | "supplier";

function documentErrorKey(kind: SettlementDocumentKind): string {
  return kind === "supplier"
    ? "settlements.detail.downloadSupplierInvoiceError"
    : "settlements.detail.downloadFePdfError";
}

export default function SettlementPayeeDetailPage() {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const { userId = "" } = useParams();
  const queryClient = useQueryClient();
  const hasWrite = useAuthStore((s) => s.hasPermission(Permission.SETTLEMENTS_WRITE));
  const hasSettlementDocumentsRead = useAuthStore((s) =>
    s.hasPermission(Permission.SETTLEMENT_DOCUMENTS_READ),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [flowFilter, setFlowFilter] = useState<SettlementFlowFilter>("all");
  const [paymentFilter, setPaymentFilter] = useState<SettlementPaymentFilter>("all");
  const [invoiceFilter, setInvoiceFilter] = useState<SettlementInvoiceFilter>("all");
  const [activeMetric, setActiveMetric] = useState<SettlementMetricFilter | null>(null);
  const [documentBusy, setDocumentBusy] = useState(false);

  const linesQuery = useQuery({
    queryKey: adminSettlementKeys.payeeLines(userId),
    queryFn: () => adminSettlementService.getPayeeLines(userId),
    enabled: Boolean(userId),
  });

  const activeRevolutQuery = useQuery({
    queryKey: adminSettlementKeys.activeRevolutPayout(userId),
    queryFn: () => adminSettlementService.getActiveRevolutPayout(userId),
    enabled: Boolean(userId),
  });

  useEffect(() => {
    const loadError = linesQuery.error ?? activeRevolutQuery.error;
    if (loadError) {
      toastApiError(loadError, "generic", t("settlements.detail.loadError"));
    }
  }, [linesQuery.error, activeRevolutQuery.error, t]);

  const payee = linesQuery.data?.payee ?? null;
  const lines = linesQuery.data?.lines ?? EMPTY_LINES;
  const activeRevolutPayout = activeRevolutQuery.data?.payout ?? null;
  const loading = linesQuery.isLoading || activeRevolutQuery.isLoading;
  const isFetching = linesQuery.isFetching || activeRevolutQuery.isFetching;

  const resetFilters = useCallback(() => {
    setFlowFilter("all");
    setPaymentFilter("all");
    setInvoiceFilter("all");
    setActiveMetric(null);
  }, []);

  const refreshDetail = useCallback(
    async (opts: { resetFilters: boolean }) => {
      if (!userId) return;
      if (opts.resetFilters) resetFilters();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminSettlementKeys.payeeLines(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: adminSettlementKeys.activeRevolutPayout(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: adminSettlementKeys.payeeLists(),
        }),
      ]);
      const fresh = queryClient.getQueryData<SettlementPayeeLinesResponse>(
        adminSettlementKeys.payeeLines(userId),
      );
      if (fresh) {
        setSelected((prev) => selectionAfterReload(prev, fresh.lines));
      }
    },
    [queryClient, resetFilters, userId],
  );

  const handleRefresh = useCallback(() => {
    resetFilters();
    void Promise.all([linesQuery.refetch(), activeRevolutQuery.refetch()]).then(
      ([linesResult]) => {
        if (linesResult.data) {
          setSelected((prev) => selectionAfterReload(prev, linesResult.data.lines));
        }
      },
    );
  }, [activeRevolutQuery, linesQuery, resetFilters]);

  useRevolutPayoutPolling(userId, activeRevolutPayout, refreshDetail);

  const filteredLines = useMemo(
    () =>
      sortSettlementLinesByEventDate(
        filterSettlementLines(lines, flowFilter, paymentFilter, invoiceFilter),
      ),
    [lines, flowFilter, paymentFilter, invoiceFilter],
  );

  const selectedLines = useMemo(
    () => lines.filter((line) => selected.has(line.id)),
    [lines, selected],
  );
  const selectedLinesReadOnly =
    selectedLines.length > 0 && selectedLines.every(isSettlementLineFinalizedAndPaid);

  const totals = useMemo(
    () => ({
      currency: payee?.currency ?? "EUR",
      ...settlementSelectionTotals(selectedLines),
    }),
    [payee?.currency, selectedLines],
  );

  const actions = useSettlementActions({
    userId,
    selected,
    selectedLines,
    payableCents: totals.payable,
    refreshDetail,
  });

  const { flow, mixedSelection } = useMemo(
    () => resolveSelectionFlow(selectedLines),
    [selectedLines],
  );

  const summaryStats = useMemo(
    () => settlementSummaryEmailStats(selectedLines),
    [selectedLines],
  );

  const blockingRevolutOpen =
    !!activeRevolutPayout && !isTerminalRevolutStatus(activeRevolutPayout.status);

  const toggleLine = (lineId: string) => {
    setActiveMetric(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const applyLineFilters = (next: {
    flow?: SettlementFlowFilter;
    payment?: SettlementPaymentFilter;
    invoice?: SettlementInvoiceFilter;
  }) => {
    setActiveMetric(null);
    if (next.flow !== undefined) {
      setFlowFilter(next.flow);
      setPaymentFilter(next.payment ?? "all");
      setInvoiceFilter(next.invoice ?? "all");
      return;
    }
    if (next.payment !== undefined) setPaymentFilter(next.payment);
    if (next.invoice !== undefined) setInvoiceFilter(next.invoice);
  };

  const selectLinesForMetric = useCallback(
    async (metric: SettlementMetricFilter) => {
      if (!userId) return;
      try {
        const res = await queryClient.fetchQuery({
          queryKey: adminSettlementKeys.payeeLines(userId),
          queryFn: () => adminSettlementService.getPayeeLines(userId),
          staleTime: 0,
        });
        setFlowFilter("all");
        setPaymentFilter("all");
        setInvoiceFilter("all");
        const ids = settlementLineIdsForMetric(res.lines, metric);
        setSelected(new Set(ids));
        setActiveMetric(metric);
      } catch (error) {
        toastApiError(error, "generic", t("settlements.detail.loadError"));
      }
    },
    [queryClient, t, userId],
  );

  const openSettlementDocument = (
    kind: SettlementDocumentKind,
    documentId: string,
    mode: "view" | "download",
  ) => {
    if (documentBusy) return;
    setDocumentBusy(true);
    void openOrDownload({
      mode,
      baseName:
        kind === "supplier"
          ? `supplier-invoice-${documentId.slice(0, 8)}`
          : `commission-invoice-${documentId.slice(0, 8)}`,
      fetchBlob: () =>
        kind === "supplier"
          ? adminSettlementService.downloadSupplierInvoice(documentId)
          : adminSettlementService.downloadSettlementDocumentPdf(documentId),
    })
      .catch((error: unknown) => {
        toastApiError(error, "generic", t(documentErrorKey(kind)));
      })
      .finally(() => {
        setDocumentBusy(false);
      });
  };

  const handleViewFePdf = (documentId: string) => {
    openSettlementDocument("commission", documentId, "view");
  };

  const handleDownloadFePdf = (documentId: string) => {
    openSettlementDocument("commission", documentId, "download");
  };

  const handleViewSupplierInvoice = (documentId: string) => {
    openSettlementDocument("supplier", documentId, "view");
  };

  const handleDownloadSupplierInvoice = (documentId: string) => {
    openSettlementDocument("supplier", documentId, "download");
  };

  if (loading && !payee) {
    return (
      <PageFrame
        backLink={{
          fallback: "/admin/payments/settlements",
          children: t("settlements.detail.back"),
        }}
        isLoading
        loadingMessage={t("settlements.detail.loading")}
      />
    );
  }

  const busy = actions.submitting !== null || documentBusy;
  const noSelection = selected.size === 0;

  return (
    <PageFrame
      backLink={{
        fallback: "/admin/payments/settlements",
        children: t("settlements.detail.back"),
      }}
    >
      {payee && (
        <SettlementPayeeHeader
          payee={payee}
          flow={flow}
          activeMetric={activeMetric}
          onSelectMetric={(metric) => void selectLinesForMetric(metric)}
          actions={
            <Button
              type="button"
              variant="outline"
              isLoading={isFetching}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={handleRefresh}
            >
              {tCommon("refresh")}
            </Button>
          }
        />
      )}

      {hasWrite && blockingRevolutOpen && activeRevolutPayout && (
        <SettlementRevolutBanner payout={activeRevolutPayout} />
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start xl:gap-5">
        <SettlementLinesSection
              lines={filteredLines}
              totalLineCount={lines.length}
              selected={selected}
              flowFilter={flowFilter}
              paymentFilter={paymentFilter}
              invoiceFilter={invoiceFilter}
              hasSettlementDocumentsRead={hasSettlementDocumentsRead}
              busy={busy}
              onToggleLine={toggleLine}
              onSelectionChange={setSelected}
              onFlowFilterChange={(filter) => applyLineFilters({ flow: filter })}
              onPaymentFilterChange={(filter) => applyLineFilters({ payment: filter })}
              onInvoiceFilterChange={(filter) => applyLineFilters({ invoice: filter })}
              onSelectAll={() => {
                setActiveMetric(null);
                setSelected(new Set(filteredLines.map((line) => line.id)));
              }}
              onDeselectAll={() => {
                setActiveMetric(null);
                setSelected(new Set());
              }}
              onViewFePdf={handleViewFePdf}
              onDownloadFePdf={handleDownloadFePdf}
              onViewSupplierInvoice={handleViewSupplierInvoice}
              onDownloadSupplierInvoice={handleDownloadSupplierInvoice}
            />

        <SettlementActionSteps
          flow={flow}
          selectedLines={selectedLines}
          hasWrite={hasWrite}
          busy={busy}
          noSelection={noSelection}
          readOnly={selectedLinesReadOnly}
          mixedSelection={mixedSelection}
          selectedCount={selected.size}
          submitting={actions.submitting}
          totals={totals}
          summaryStats={summaryStats}
          payee={payee}
          blockingRevolutOpen={blockingRevolutOpen}
          onSendSummary={actions.sendSummary}
          onRecordCollection={actions.recordCollection}
          onRecordSupplierInvoice={actions.recordSupplierInvoice}
          onReplaceSupplierInvoice={actions.replaceSupplierInvoice}
          onViewSupplierInvoice={
            hasSettlementDocumentsRead ? handleViewSupplierInvoice : undefined
          }
          onDownloadSupplierInvoice={
            hasSettlementDocumentsRead ? handleDownloadSupplierInvoice : undefined
          }
          onMarkInvoiced={actions.markInvoiced}
          onMarkPaid={actions.markPaid}
          onRevolutPayout={actions.startRevolutPayout}
        />
      </div>
    </PageFrame>
  );
}
